import { useReducer } from 'react'
import {
  ActionImportSource,
  ExtractActionsResponse,
} from '../../../types/actionImportTypes'
import {
  EMPTY_CONTEXT,
  ImportContext,
  ImportRow,
  applyContextChange,
  cloneContext,
  emptyRow,
  rowFromExtracted,
} from './importRowModel'

export type WizardStep = 'source' | 'context' | 'review' | 'result'

export const WIZARD_STEPS: WizardStep[] = ['source', 'context', 'review', 'result']

export type ExtractionState = {
  status: 'idle' | 'loading' | 'success' | 'error'
  /** Identifies what was extracted (source + file), to avoid re-running it. */
  key: string
  error?: string
  fileName?: string | null
  total?: number
}

export type JiraState = {
  status: 'idle' | 'connecting' | 'connected' | 'error'
  error?: string
}

export type FailedImport = { rowId: string; name: string; message: string }

export type ImportRunState = {
  status: 'idle' | 'running' | 'done'
  total: number
  created: number
  failed: FailedImport[]
}

export type WizardState = {
  step: WizardStep
  source: ActionImportSource | null
  file: File | null
  jira: JiraState
  context: ImportContext
  /** Defaults that were applied to the current rows. */
  appliedContext: ImportContext
  labels: Record<string, string>
  extraction: ExtractionState
  rows: ImportRow[]
  lastDeleted: { rows: ImportRow[]; indexes: number[] } | null
  run: ImportRunState
}

export const INITIAL_STATE: WizardState = {
  step: 'source',
  source: null,
  file: null,
  jira: { status: 'idle' },
  context: EMPTY_CONTEXT,
  appliedContext: EMPTY_CONTEXT,
  labels: {},
  extraction: { status: 'idle', key: '' },
  rows: [],
  lastDeleted: null,
  run: { status: 'idle', total: 0, created: 0, failed: [] },
}

export function extractionKey(
  source: ActionImportSource | null,
  file: File | null
): string {
  if (!source) return ''
  if (source === 'jira') return 'jira'
  return file ? `${source}:${file.name}:${file.size}:${file.lastModified}` : ''
}

export type WizardAction =
  | { type: 'reset'; context?: ImportContext }
  | { type: 'goTo'; step: WizardStep }
  | { type: 'selectSource'; source: ActionImportSource }
  | { type: 'setFile'; file: File | null }
  | { type: 'jiraConnecting' }
  | { type: 'jiraFailed'; error: string }
  | { type: 'setContext'; context: ImportContext }
  | { type: 'mergeLabels'; labels: Record<string, string> }
  | { type: 'applyContextToRows' }
  | { type: 'extractStart'; key: string }
  | { type: 'extractSuccess'; key: string; response: ExtractActionsResponse }
  | { type: 'extractError'; key: string; error: string }
  | { type: 'updateRow'; id: string; patch: Partial<ImportRow> }
  | { type: 'patchRows'; ids: string[]; patch: Partial<ImportRow> }
  | { type: 'deleteRows'; ids: string[] }
  | { type: 'undoDelete' }
  | { type: 'addRow' }
  | { type: 'importStart'; total: number }
  | { type: 'importOutcome'; rowId: string; ok: boolean; message?: string }
  | { type: 'importDone' }
  | { type: 'retryFailed' }

function reducer(state: WizardState, action: WizardAction): WizardState {
  switch (action.type) {
    case 'reset': {
      const context = cloneContext(action.context ?? EMPTY_CONTEXT)
      return {
        ...INITIAL_STATE,
        context,
        appliedContext: cloneContext(context),
      }
    }

    case 'goTo':
      return { ...state, step: action.step }

    case 'selectSource':
      if (state.source === action.source) return state
      return {
        ...state,
        source: action.source,
        file: null,
        jira: { status: 'idle' },
      }

    case 'setFile':
      return { ...state, file: action.file }

    case 'jiraConnecting':
      return {
        ...state,
        jira: { status: 'connecting' },
        extraction: { status: 'loading', key: 'jira' },
      }

    case 'jiraFailed':
      return {
        ...state,
        jira: { status: 'error', error: action.error },
        extraction: { status: 'error', key: 'jira', error: action.error },
      }

    case 'setContext':
      return { ...state, context: cloneContext(action.context) }

    case 'mergeLabels':
      return { ...state, labels: { ...state.labels, ...action.labels } }

    case 'applyContextToRows':
      return {
        ...state,
        rows: state.rows.map((row) =>
          applyContextChange(row, state.appliedContext, state.context)
        ),
        appliedContext: cloneContext(state.context),
      }

    case 'extractStart':
      return {
        ...state,
        extraction: { status: 'loading', key: action.key },
      }

    case 'extractSuccess': {
      if (action.key !== state.extraction.key) return state
      const rows = action.response.tasks.map((task) =>
        rowFromExtracted(task, state.context)
      )
      return {
        ...state,
        jira: action.key === 'jira' ? { status: 'connected' } : state.jira,
        extraction: {
          status: 'success',
          key: action.key,
          fileName: action.response.file_name,
          total: action.response.total_count,
        },
        rows,
        appliedContext: cloneContext(state.context),
        lastDeleted: null,
      }
    }

    case 'extractError':
      if (action.key !== state.extraction.key) return state
      return {
        ...state,
        extraction: { status: 'error', key: action.key, error: action.error },
      }

    case 'updateRow':
      return {
        ...state,
        rows: state.rows.map((row) =>
          row.id === action.id
            ? { ...row, ...action.patch, serverError: undefined }
            : row
        ),
      }

    case 'patchRows': {
      const ids = new Set(action.ids)
      return {
        ...state,
        rows: state.rows.map((row) =>
          ids.has(row.id)
            ? { ...row, ...action.patch, serverError: undefined }
            : row
        ),
      }
    }

    case 'deleteRows': {
      const ids = new Set(action.ids)
      const removed: ImportRow[] = []
      const indexes: number[] = []
      state.rows.forEach((row, index) => {
        if (ids.has(row.id)) {
          removed.push(row)
          indexes.push(index)
        }
      })
      if (!removed.length) return state
      return {
        ...state,
        rows: state.rows.filter((row) => !ids.has(row.id)),
        lastDeleted: { rows: removed, indexes },
      }
    }

    case 'undoDelete': {
      if (!state.lastDeleted) return state
      const rows = [...state.rows]
      state.lastDeleted.indexes.forEach((index, i) => {
        rows.splice(Math.min(index, rows.length), 0, state.lastDeleted!.rows[i])
      })
      return { ...state, rows, lastDeleted: null }
    }

    case 'addRow':
      return { ...state, rows: [emptyRow(state.context), ...state.rows] }

    case 'importStart':
      return {
        ...state,
        step: 'result',
        lastDeleted: null,
        run: { status: 'running', total: action.total, created: 0, failed: [] },
      }

    case 'importOutcome': {
      if (action.ok) {
        return {
          ...state,
          rows: state.rows.filter((row) => row.id !== action.rowId),
          run: { ...state.run, created: state.run.created + 1 },
        }
      }
      const row = state.rows.find((r) => r.id === action.rowId)
      return {
        ...state,
        rows: state.rows.map((r) =>
          r.id === action.rowId ? { ...r, serverError: action.message } : r
        ),
        run: {
          ...state.run,
          failed: [
            ...state.run.failed,
            {
              rowId: action.rowId,
              name: row?.ActionName || '',
              message: action.message || '',
            },
          ],
        },
      }
    }

    case 'importDone':
      return { ...state, run: { ...state.run, status: 'done' } }

    case 'retryFailed':
      return {
        ...state,
        step: 'review',
        run: { status: 'idle', total: 0, created: 0, failed: [] },
      }

    default:
      return state
  }
}

export function useImportWizard() {
  return useReducer(reducer, INITIAL_STATE)
}
